import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Vigilante único: marca data-shown en [data-reveal] la primera vez que entra
// en pantalla y pausa las auroras que no se ven. Reescanea al cambiar de ruta
// y cuando React añade contenido. Usa atributos (no className) para que React
// no los borre al volver a pintar.
export default function MotionRuntime() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.__mvReady = true;
    const root = document.documentElement;
    if (!("IntersectionObserver" in window)) return undefined;
    // Al cambiar de idioma React vuelve a crear <html> y se pierde la clase que
    // puso el arranque: se repone con la misma condición.
    const canMotion = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (canMotion) root.classList.add("motion-ok");

    const reveal = canMotion && new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.setAttribute("data-shown", "");
          reveal.unobserve(e.target);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );
    const aurora = new IntersectionObserver((entries) => {
      for (const e of entries) e.target.toggleAttribute("data-paused", !e.isIntersecting);
    });

    const scan = () => {
      if (reveal) {
        document.querySelectorAll("[data-reveal]:not([data-shown]):not([data-mv-watch])").forEach((el) => {
          el.setAttribute("data-mv-watch", "");
          reveal.observe(el);
        });
      }
      document.querySelectorAll(".aurora:not([data-mv-watch])").forEach((el) => {
        el.setAttribute("data-mv-watch", "");
        aurora.observe(el);
      });
    };
    scan();

    let raf = 0;
    const mutations = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(scan);
    });
    mutations.observe(document.body, { childList: true, subtree: true });

    const onVisibility = () => root.toggleAttribute("data-mv-hidden", document.hidden);
    document.addEventListener("visibilitychange", onVisibility);

    // Red de seguridad: WebKit (Safari, iPhone) a veces no avisa de lo que
    // cruza la pantalla durante un scroll continuo o con inercia. Cuando el
    // scroll se para, se muestra lo que esté a la vista y siga pendiente.
    let idle = 0;
    const sweep = () => {
      if (!reveal) return;
      const vh = window.innerHeight;
      document.querySelectorAll("[data-reveal][data-mv-watch]:not([data-shown])").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom > 0 && r.top < vh * 0.9 && r.width > 0) {
          el.setAttribute("data-shown", "");
          reveal.unobserve(el);
        }
      });
    };
    const onScroll = () => {
      clearTimeout(idle);
      idle = setTimeout(sweep, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      if (reveal) reveal.disconnect();
      aurora.disconnect();
      mutations.disconnect();
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(idle);
      document.querySelectorAll("[data-mv-watch]").forEach((el) => el.removeAttribute("data-mv-watch"));
    };
  }, [pathname]);

  return null;
}
